<script setup lang="ts">
import { createErrorMessage } from '@errors/helpers';
import { usePolisGetWagenparkcontract } from '@services/polis-public/orval-api/polis-api/polis-api';
import VCollapsibleSection from '@shared/component/VCollapsibleSection/VCollapsibleSection.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSimpleTable from '@shared/component/VSimpleTable/VSimpleTable.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VStatusTag from '@shared/component/VStatusTag/VStatusTag.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { computed, toRefs } from 'vue';

import {
  type AfspraakKolom,
  type DekkingRij,
  formatCel,
  formatPeriode,
  maakDekkingRijen,
  zichtbareKolommen,
} from './wagenparkcontractAfspraken';

const props = defineProps<{
  contractnummer: number;
}>();

const { contractnummer } = toRefs(props);

const open = defineModel<boolean>('open', { default: true });

const {
  data: contract,
  isPending,
  isError,
  error,
} = usePolisGetWagenparkcontract(contractnummer, {
  query: {
    select: (result) => result.result,
    retry: 3,
    staleTime: 5 * 60 * 1000,
  },
});

type Afsprakentabel = {
  naam: string;
  wagenparktype: string;
  rijen: DekkingRij[];
  kolommen: AfspraakKolom[];
  rijPerKey: Map<string, DekkingRij>;
  kolomPerKey: Map<string, AfspraakKolom>;
};

const afsprakentabellen = computed<Afsprakentabel[]>(
  () =>
    contract.value?.voertuigsoorten
      .map((voertuigsoort) => {
        const rijen = maakDekkingRijen(voertuigsoort);
        const kolommen = zichtbareKolommen(rijen);

        return {
          naam: voertuigsoort.naam,
          wagenparktype: voertuigsoort.wagenparktype,
          rijen,
          kolommen,
          rijPerKey: new Map(rijen.map((rij) => [rij.key, rij])),
          kolomPerKey: new Map(kolommen.map((kolom) => [kolom.key, kolom])),
        };
      })
      .filter((tabel) => tabel.rijen.length > 0) ?? [],
);

const looptijd = computed(() =>
  contract.value ? formatPeriode(contract.value.ingangsdatum, contract.value.einddatum) : '',
);

function cel(tabel: Afsprakentabel, rijKey: string, kolomKey: string): string {
  const rij = tabel.rijPerKey.get(rijKey);
  const kolom = tabel.kolomPerKey.get(kolomKey);

  return rij && kolom ? formatCel(rij, kolom) : '';
}
</script>

<template>
  <VCollapsibleSection v-model:open="open" class="px-6" data-testid="wagenparkcontract-afspraken">
    <template #title>
      <div class="px-2">Contractafspraken</div>
    </template>
    <template #content>
      <VInfoBox badge-text="error" type="danger" v-if="isError">
        <div
          v-sanitized-html="
            createErrorMessage(error, {
              onNotFoundError: 'Het bijbehorende wagenparkcontract kon niet gevonden worden.',
            })
          "
        ></div>
      </VInfoBox>
      <VSkeleton v-else-if="isPending" height="lg" :count="3" />
      <div v-else-if="contract" class="px-2 py-6">
        <dl class="mb-6 grid gap-y-2 lg:grid-cols-[auto_1fr] lg:gap-x-10">
          <dt class="font-bold text-primary-900">Contractnummer</dt>
          <dd data-testid="wagenparkcontract-contractnummer">{{ contract.contractnummer }}</dd>
          <template v-if="contract.omschrijving">
            <dt class="font-bold text-primary-900">Omschrijving</dt>
            <dd>{{ contract.omschrijving }}</dd>
          </template>
          <dt class="font-bold text-primary-900">Looptijd</dt>
          <dd>{{ looptijd }}</dd>
        </dl>

        <div
          v-for="tabel in afsprakentabellen"
          :key="tabel.naam"
          :data-testid="`wagenparkcontract-voertuigsoort-${tabel.naam}`"
        >
          <VHeading :level="3.0" appearance="sm" class="mb-2 flex items-center gap-4">
            {{ tabel.naam }}
            <VStatusTag color="grey">{{ tabel.wagenparktype }}</VStatusTag>
          </VHeading>

          <VSimpleTable
            :rows="tabel.rijen"
            :columns="tabel.kolommen"
            full-width
            align="right"
            row-header-align="left"
            class="mb-4"
          >
            <template #corner>Dekking</template>
            <template #row-header="{ row }">
              <div>{{ row.label }}</div>
              <div v-if="tabel.rijPerKey.get(row.key)?.product" class="text-xs text-grey-700">
                {{ tabel.rijPerKey.get(row.key)?.product }}
              </div>
              <div v-if="tabel.rijPerKey.get(row.key)?.periode" class="text-xs text-grey-700">
                {{ tabel.rijPerKey.get(row.key)?.periode }}
              </div>
            </template>
            <template #cell="{ row, column }">
              {{ cel(tabel, row.key, column.key) }}
            </template>
          </VSimpleTable>
        </div>
      </div>
    </template>
  </VCollapsibleSection>
</template>
